import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion, Reorder, useDragControls } from 'motion/react'
import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { DragAndDrop6DotsIcon } from '@blade/ui-kit'
import { SummaryChartMiniature } from '@root/entities/summary/ui/summary-chart/desktop/summary-chart-miniature'
import { useTranslation } from '@root/shared/localization'
import { Checkbox } from '@root/shared/ui-kit'

import {
  WALLET_ASSET_TABLE_HEADER_HEIGHT,
  WALLET_ASSET_TABLE_ROW_HEIGHT,
  WalletAssetTable,
} from './wallet-assets-table/wallet-assets-table'
import { WalletsTableAddressCell } from './wallets-table-address-cell'
import { WalletsTableAssetsCell } from './wallets-table-assets-cell'
import { WalletsTableBalanceCell } from './wallets-table-balance-cell'
import { EditButton } from './wallets-table-edit-cell'
import { WalletsTableTransactionsCell } from './wallets-table-transactions-cell'
import { WalletsTableWalletCell } from './wallets-table-wallet-cell'

import type { WalletModel } from '@root/entities/wallet'

const ROW_HEIGHT = 50
type Props = {
  wallet: WalletModel
  expanded: boolean
  onExpandToggle: () => void
  style: React.CSSProperties
  onToggleSelection: (wallet: WalletModel) => void
  onSelectWallet: (wallet: WalletModel) => void
  onTransactionsClick: (wallet: WalletModel) => void
  onEditClick: (wallet: WalletModel) => void
  onDragEnd: () => void
  selected: boolean
  faded: boolean
  bodyHeight: number
  withSelection?: boolean
  withTransactions?: boolean
}

export const WalletsTableRow = ({
  wallet,
  onExpandToggle,
  expanded,
  style,
  onToggleSelection,
  onSelectWallet,
  onTransactionsClick,
  onEditClick,
  selected,
  faded,
  bodyHeight,
  withSelection = true,
  withTransactions = true,
  onDragEnd,
}: Props) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const dragControls = useDragControls()
  const [isDragging, setIsDragging] = useState(false)

  const [assets] = useAtom((ctx) => ctx.spy(wallet.assets).length)

  const assetsTableHeight = useMemo(() => {
    return (
      assets * WALLET_ASSET_TABLE_ROW_HEIGHT +
      WALLET_ASSET_TABLE_HEADER_HEIGHT +
      1
    )
  }, [assets])

  const height = expanded
    ? Math.min(assetsTableHeight + ROW_HEIGHT, bodyHeight)
    : ROW_HEIGHT

  return (
    <Root
      forwardedAs="div"
      value={wallet.id}
      dragListener={false}
      dragControls={dragControls}
      ref={ref}
      aria-checked={selected}
      data-expanded={expanded}
      data-faded={faded}
      data-dragging={isDragging}
      animate={{ height }}
      transition={{ duration: 0.2 }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      onAnimationComplete={() => {
        if (expanded) {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }}
    >
      <Inner
        data-faded={faded}
        data-checked={selected}
        onClick={() => onSelectWallet(wallet)}
      >
        <AnimatePresence initial={false}>
          {selected ? (
            <SelectionHighlight
              initial={{ opacity: 0, scaleX: 0.5 }}
              animate={{ opacity: 1, scaleX: 1 }}
              exit={{ opacity: 0, scaleX: 0.5 }}
              transition={{ duration: 0.15 }}
            >
              <HighlightBorder />
            </SelectionHighlight>
          ) : undefined}
        </AnimatePresence>

        <InnerContent style={style}>
          {withSelection ? (
            <Checkbox
              checked={selected}
              onClick={(e) => {
                e.stopPropagation()
                onToggleSelection(wallet)
              }}
            />
          ) : undefined}
          <WalletsTableWalletCell wallet={wallet} />
          <WalletsTableAddressCell wallet={wallet} />
          {withTransactions ? (
            <WalletsTableTransactionsCell
              wallet={wallet}
              onTransactionsClick={onTransactionsClick}
            />
          ) : undefined}
          <WalletsTableBalanceCell wallet={wallet} />
          <WalletsTableAssetsCell
            wallet={wallet}
            onExpandToggle={onExpandToggle}
            expanded={expanded}
          />
          <div>
            <Chart wallet={wallet} />
          </div>
          <Actions>
            <EditButton wallet={wallet} onEditClick={onEditClick} />
            <DragHandle
              aria-label={t('wallet.reorder-aria-label')}
              onPointerDown={(e) => {
                e.stopPropagation()
                dragControls.start(e)
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <DragAndDrop6DotsIcon size={14} color="var(--text-10)" />
            </DragHandle>
          </Actions>
        </InnerContent>
      </Inner>
      <AnimatePresence>
        {expanded ? (
          <ExpandedContent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <WalletAssetTable wallet={wallet} />
          </ExpandedContent>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Inner = styled.div`
  height: ${ROW_HEIGHT}px;
  font-size: 12px;

  position: relative;
  cursor: pointer;
  transition: background 0.2s ease-in-out;
  &[data-checked='true'] {
    background: var(--appearance-1);
  }
`

const SelectionHighlight = styled(motion.div)`
  display: block;
  position: absolute;
  width: 120px;
  inset: 0;
  right: auto;
  border-inline-start: 2px solid var(--primary-9);
  transform-origin: left center;

  background: linear-gradient(
    90deg,
    var(--primary-4) 0%,
    transparent 37px,
    transparent 100%
  );
`

const HighlightBorder = styled.div`
  position: absolute;
  width: 120px;
  height: 1px;
  top: 0;
  left: 0;
  background: linear-gradient(90deg, var(--primary-9) 0%, transparent 100%);
`

const InnerContent = styled.div`
  display: grid;
  justify-items: start;
  align-items: center;
  width: 100%;
  height: 100%;
  padding-inline: 18px 8px;
  position: relative;
`

const Root = styled(Reorder.Item)`
  transition: opacity 0.2s linear;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  position: relative;

  &:nth-child(even) {
    ${Inner} {
      background: var(--appearanceA-2, var(--appearanceA-2));
    }
  }
  &:nth-child(odd) {
    ${Inner} {
      background: var(--appearanceA-4, var(--appearanceA-4));
    }
  }
  &[data-expanded='true'] {
    background: var(--appearanceA-4, var(--appearanceA-4));
    ${Inner} {
      background: var(--appearance-1);
    }
  }
  &[data-expanded='false']:hover {
    ${Inner} {
      background: var(--backgroundA-1);
    }
  }
  &[data-faded='true'] {
    pointer-events: none;
    opacity: 0.25;
  }
  &&[data-dragging='true'] {
    ${Inner} {
      background: var(--appearance-1);
      box-shadow: 0 0 2px 2px var(--appearanceA-1);
    }
  }
`

const DragHandle = styled.button`
  height: ${ROW_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  color: inherit;
  cursor: grab;
  touch-action: none;
  z-index: 1;
  padding-inline: 4px;

  &:active {
    cursor: grabbing;
  }
`

const ExpandedContent = styled(motion.div)`
  display: grid;
`

const Chart = styled(SummaryChartMiniature)`
  width: 116px;
  height: 32px;
`

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-self: end;
  gap: 4px;
  align-items: center;
`
